import moment, { type Moment } from 'moment'

import { ScheduleDayLookup, type ScheduleDay } from 'types/schedules/schedules'
import { type Time24Hour, type MomentParsableDate } from 'types/time'
import { pluralize } from 'utils/strings'
import { WEEK_DAYS } from 'utils/time/days'

/**
 * Given a start and end time, calculate the duration of hours between them. Accounts cases
 * where the end time carries over into the next day. Value is returned in hours.
 */
export function getScheduleWindowDuration(
  startTime: MomentParsableDate,
  endTime: MomentParsableDate
) {
  const startTimeMoment = moment.utc(startTime)
  const endTimeMoment = moment.utc(endTime)

  if (endTimeMoment.isBefore(startTimeMoment)) {
    endTimeMoment.add(1, 'days')
  }

  return endTimeMoment.diff(startTimeMoment, 'hours')
}

/**
 * Calculates the new scheduled end time based on a provided duration and start time.
 */
export function getScheduleWindowEndTime(
  duration: string | number,
  startTime: MomentParsableDate
) {
  // Parses the field value into a number (TS seems to think this could be a string, perhaps incorrectly)
  const parsedDuration =
    typeof duration === 'string' ? parseInt(duration, 10) : duration
  const momentStart = moment.utc(startTime)
  return moment(momentStart).clone().add(parsedDuration, 'hours')
}

export function getScheduleWindowHelperText(
  activeDays: ScheduleDay[],
  start: MomentParsableDate,
  end: MomentParsableDate
) {
  const duration = getScheduleWindowDuration(start, end)
  const hoursText = pluralize('hour', duration)
  const startTime = moment.utc(start).format('h:mm A')
  const activeDaysString = activeDays
    .map((day) => ScheduleDayLookup[day])
    .join(', ')

  return `This schedule will run every ${activeDaysString} for ${duration} ${hoursText} each day, starting at ${startTime}.`
}

/**
 * Adjust a given schedule's start and end times based on the new start time provided.
 */
export function getUpdatedScheduleWindowTimes(
  startTime: Moment,
  prevStartTime: MomentParsableDate,
  prevEndTime: MomentParsableDate
): { end: Moment; start: Moment } {
  // Get timezone-adjusted moment instances of the start and end field values.
  const startTimeMoment = moment.utc(prevStartTime)
  const newStartTimeMoment = moment.utc(startTime)
  // Derive new end time offsets for both hours and any roll-over days.
  const hoursDelta = newStartTimeMoment.diff(startTimeMoment, 'hours')
  // Creates new end time Moment instance with the proper hour adjustment.
  const endTimeMoment = moment.utc(prevEndTime).add(hoursDelta, 'hours')

  return {
    start: newStartTimeMoment,
    end: endTimeMoment
  }
}

/**
 * Given start and end times in Time24Hour (HH:mm) format and a ScheduleDay, return Moment instances
 * representing the start and end times on that day. Accounts for end times that roll over into
 * the next day.
 */
export function getScheduleWindowTimesFromTruncatedFormat(
  startTime: Time24Hour,
  endTime: Time24Hour,
  day: ScheduleDay
) {
  const dayIndex = WEEK_DAYS.indexOf(day)
  const startMoment = moment.utc(startTime, 'HH:mm').day(dayIndex)
  const endMoment = moment
    .utc(endTime, 'HH:mm')
    .day(dayIndex)
    .add(startTime > endTime ? 1 : 0, 'day')

  return { start: startMoment, end: endMoment }
}

/**
 * Returns the difference (in hours) between two timezones. Timezone strings should be
 * in a format recognized by moment-timezone.
 */
export function getTimezoneHoursOffset(timezone: string, comparator: string) {
  const zoneToTest = moment.tz.zone(timezone)
  const comparatorZone = moment.tz.zone(comparator)

  if (!zoneToTest || !comparatorZone) return 0

  // NOTE: `zone.parse()` returns the offset in minutes, so we convert to hours here.
  return (
    (comparatorZone.parse(moment().milliseconds()) -
      zoneToTest.parse(moment().milliseconds())) /
    60
  )
}
